import React, { useEffect, useState, useMemo, useCallback } from 'react';
import styles from './SummaryPage.module.css';

import Adshel from '../Panels/Adshel/Adshel.jsx';
import AdshelClassic from '../Panels/AdshelClassic/AdshelClassic.jsx';
import SpectacularPortrait from '../Panels/SpectacularPortrait/SpectacularPortrait.jsx';
import Billboard from '../Panels/Billboard/Billboard.jsx';
import ThreeGroup from '../Panels/3Group/3Group.jsx';
import Escalator from '../Panels/Escalator/Escalator.jsx';
import Storm from '../Panels/Storm/Storm.jsx';
import Overview from './Overview/Overview.jsx';
import Details from './Details/Details.jsx';

import {
    CAMPAIGN_STATUS,
    CAMPAING_PROMISE_TYPES,
    COUNTRY_CODES,
    PRODUCT_TYPES,
    CREATIVE_MIMES,
    AD_TYPES,
    PANEL_TYPES,
    CAMPAIGN_PROMISE_EXTRA_TEXT_TYPES
} from '../../../constants/enums';
import campaignValues from '../../../utils/campaignValues.js';
import NumberRounding from '../../../utils/numberRounding.js';
import dateFunctions from '../../../utils/dateFunctions.js';
import QueryStringService from '../../../services/queryStringService.js';
import { useLp5Configuration } from '../Contexts/Lp5ConfigurationContext';

const IMAGE_SOURCES = {
    ADSHEL_PANEL: require('../../../images/Illustration_Play_Adshel.png'),
    BILLBOARD_PANEL: require('../../../images/Illustration_Play_Billboard.png'),
    SPECTACULAR_PORTRAIT_PANEL: require('../../../images/Illustration_Play_Spectacular_portrait.png'),
    ADSHEL_NO_AD_COPY: require('../../../images/adshel_missing_adcopy.png'),
    ADSHEL_NO_TEXT: require('../../../images/adshel_no_text.png'),
    THREE_GROUP_PANEL: require('../../../images/Illustration_Classic_Group_of_3.png'),
    STORM_PANEL: require('../../../images/Illustration_Play_Storm.png'),
    ESCALATOR_PANEL: require('../../../images/Illustration_Classic_Escalator.png'),
};

const NO_AD_COPY_DIMENSIONS = {
    HEIGHT: '1920',
    WIDTH: '1080'
};
const DEFAULT_CREATIVE_CLASSIC_SE = {
    type: AD_TYPES.IMAGE,
    src: IMAGE_SOURCES.ADSHEL_NO_TEXT,
    alt: 'no ad-copy',
    height: NO_AD_COPY_DIMENSIONS.HEIGHT,
    width: NO_AD_COPY_DIMENSIONS.WIDTH,
    panelType: PANEL_TYPES.ADSHEL_CLASSIC,
    groupingKey: crypto.randomUUID(),
    groupingIndex: 1
};

const DEFAULT_CREATIVE = {
    type: AD_TYPES.IMAGE,
    src: IMAGE_SOURCES.ADSHEL_NO_AD_COPY,
    alt: 'no ad-copy',
    height: NO_AD_COPY_DIMENSIONS.HEIGHT,
    width: NO_AD_COPY_DIMENSIONS.WIDTH,
    panelType: PANEL_TYPES.ADSHEL_PLAY,
    groupingKey: crypto.randomUUID(),
    groupingIndex: 1
};

const SummaryPage = ({
    selectedOrderlineMetaData,
    orderlinesData,
    parameters,
    campaignPromiseType,
    orderData,
    playSuccessLimit,
    postingSuccessLimit
}) => {
    const [panelTypeToShow, setPanelTypeToShow] = useState(PANEL_TYPES.ADSHEL_PLAY);
    const [campaignName, setCampaignName] = useState('');
    const { promiseConfig } = useLp5Configuration();
    const countryCode = useMemo(() => new QueryStringService().getCurrentCountry(), []);

    useEffect(() => {
        setCampaignName(
            selectedOrderlineMetaData && selectedOrderlineMetaData.length > 0 ? campaignValues.caluclateCampaignName(
                selectedOrderlineMetaData[0].alternativeName,
                orderData.customer,
                selectedOrderlineMetaData[0].periodStart,
                selectedOrderlineMetaData[0].periodEnd
            ) : '');
    }, [selectedOrderlineMetaData, orderData]);

    const getCreativeType = (mime) => {
        const lowerMime = mime?.toLowerCase() || '';
        if ([CREATIVE_MIMES.MP4, CREATIVE_MIMES.MOV].includes(lowerMime)) {
            return AD_TYPES.VIDEO;
        }
        if ([CREATIVE_MIMES.PDF].includes(lowerMime)) {
            return AD_TYPES.PDF;
        }
        return AD_TYPES.IMAGE;
    };

    const extraText = useMemo(() => {
        return campaignValues.
            fetchEtxraTextForCampaignPromise(campaignPromiseType, CAMPAIGN_PROMISE_EXTRA_TEXT_TYPES.SUMMARY_TAB);
    }, [campaignPromiseType]);

    const overviewDetailsText = useMemo(() => {
        return campaignValues.
            fetchEtxraTextForCampaignPromise(campaignPromiseType, CAMPAIGN_PROMISE_EXTRA_TEXT_TYPES.OVERVIEW_DETAILS);
    }, [campaignPromiseType]);

    // Campaign Status calculation
    const { campaignStatus, campaignStartsInDays } = useMemo(() => {
        if (!selectedOrderlineMetaData?.[0]) {
            return { campaignStatus: CAMPAIGN_STATUS.NOT_STARTED, campaignStartsInDays: 0 };
        }

        const metadata = selectedOrderlineMetaData[0];
        const idsExportFileExists = orderlinesData?.orderLineDataInformation?.idsExportFileExists ?? null;
        const status = campaignValues.campaignStatus(metadata.periodStart, metadata.periodEnd, metadata.localParameter5, idsExportFileExists);
        const daysUntilStart = status === CAMPAIGN_STATUS.NOT_STARTED
            ? Math.abs(dateFunctions.daysBetweenDates(
                metadata.periodStart,
                dateFunctions.getISOFormatFromDate(new Date())
            ))
            : 0;

        return { campaignStatus: status, campaignStartsInDays: daysUntilStart };
    }, [selectedOrderlineMetaData, orderlinesData]);

    // Chec wether to show the PanelFrame or not
    const { showPanelFrame } = useMemo(() => {

        // Never show panelFrame if it's a non MOMS campaign
        if (campaignValues.isNonMOMSFollowUpCampaign(campaignPromiseType)) {
            return { showPanelFrame: false };
        }

        if (!orderlinesData?.orderLineDataInformation) {
            return { showPanelFrame: true };
        }

        return { showPanelFrame: orderlinesData.orderLineDataInformation.showPanelFrames };
    }, [orderlinesData, campaignPromiseType]);

    // Delivery Percentage calculation
    const deliveryPercentage = useMemo(() => {
        if (!selectedOrderlineMetaData || !orderlinesData) return 0;
        return NumberRounding.round(
            campaignValues.calculateDeliveryPercentageBasedOnPromise(selectedOrderlineMetaData, orderlinesData, promiseConfig) * 100,
            0
        );
    }, [selectedOrderlineMetaData, orderlinesData, promiseConfig]);

    const mediaArray = useMemo(() => {
        if (!orderlinesData) return [DEFAULT_CREATIVE];
        let validMediaFormats = [];


        if (orderlinesData.campaignUploads.externalCreatives?.length) {
            validMediaFormats = orderlinesData.campaignUploads.externalCreatives
                .map(creative => {
                    const panelType = campaignValues.returnPanelTypesBasedOnCreativeMetadata([creative])[0];
                    if (!panelType) return null;

                    return {
                        type: getCreativeType(creative.mime),
                        src: `${parameters.externalCreativeUrl}${creative.storedAs}`,
                        alt: creative.orgFileName,
                        height: creative.height,
                        width: creative.width,
                        groupingKey: creative.groupingKey,
                        groupingIndex: creative.panelIndex,
                        panelType
                    };
                })
                .filter(item => item !== null);
        }

        if (orderlinesData.orderLineDataInformation.creatives) {
            validMediaFormats = validMediaFormats.concat(orderlinesData.orderLineDataInformation.creatives
                .map(creative => {
                    const panelType = campaignValues.returnPanelTypesBasedOnCreativeMetadata([creative])[0];
                    if (!panelType) return null;

                    return {
                        type: getCreativeType(creative.mime),
                        src: `${parameters.creativeImageUrl}${creative.id}.${creative.mime}`,
                        alt: creative.name,
                        height: creative.height,
                        width: creative.width,
                        groupingKey: crypto.randomUUID(),
                        groupingIndex: 1,
                        panelType
                    };
                })
                .filter(item => item !== null));
        }

        const isClassicSE = countryCode === COUNTRY_CODES.SWEDEN &&
            selectedOrderlineMetaData?.[0]?.type?.toLowerCase() !== PRODUCT_TYPES.PLAY;
        const fallbackCreative = isClassicSE ? DEFAULT_CREATIVE_CLASSIC_SE : DEFAULT_CREATIVE;

        if (validMediaFormats.length === 0 && campaignStatus !== CAMPAIGN_STATUS.NOT_STARTED) {
            // for classic SE use the explicit fallback image; for others let BasePanel show its own shape
            return [isClassicSE ? fallbackCreative : { ...fallbackCreative, src: '' }];
        }
        else if (validMediaFormats.length === 0 && campaignStatus === CAMPAIGN_STATUS.NOT_STARTED)
            return [fallbackCreative];
        else
            return validMediaFormats;
    }, [orderlinesData, parameters, countryCode, selectedOrderlineMetaData]);

    // Panel selection effect
    useEffect(() => {
        const panelTypes = [
            { type: PANEL_TYPES.ADSHEL_PLAY, priority: 1 },
            { type: PANEL_TYPES.SPECTACULAR_PORTRAIT_PLAY, priority: 2 },
            { type: PANEL_TYPES.BILLBOARD_PLAY, priority: 3 },
            { type: PANEL_TYPES.ESCALATOR_CLASSIC, priority: 4 },
            { type: PANEL_TYPES.THREE_GROUP_CLASSIC, priority: 5 },
            { type: PANEL_TYPES.ADSHEL_CLASSIC, priority: 6 },
            { type: PANEL_TYPES.STORM_PLAY, priority: 7 }
        ];

        const availablePanels = panelTypes.filter(
            panel => mediaArray.some(item => item.panelType === panel.type)
        );

        if (availablePanels.length) {
            setPanelTypeToShow(availablePanels.sort((a, b) => a.priority - b.priority)[0].type);
        }
    }, [mediaArray]);

    // Success check
    const isDeliverySuccessful = useMemo(() => {
        if (!selectedOrderlineMetaData?.[0]) return false;
        const isPlayType = selectedOrderlineMetaData[0].type.toLowerCase() === PRODUCT_TYPES.PLAY;
        return deliveryPercentage >= (isPlayType ? playSuccessLimit : postingSuccessLimit);
    }, [selectedOrderlineMetaData, deliveryPercentage, playSuccessLimit, postingSuccessLimit]);

    const renderPanel = () => {
        const filteredMedia = mediaArray.filter(item => item.panelType === panelTypeToShow) || [DEFAULT_CREATIVE];

        const panelComponents = {
            [PANEL_TYPES.ADSHEL_PLAY]: Adshel,
            [PANEL_TYPES.SPECTACULAR_PORTRAIT_PLAY]: SpectacularPortrait,
            [PANEL_TYPES.BILLBOARD_PLAY]: Billboard,
            [PANEL_TYPES.STORM_PLAY]: Storm,
            [PANEL_TYPES.ESCALATOR_CLASSIC]: Escalator,
            [PANEL_TYPES.THREE_GROUP_CLASSIC]: ThreeGroup,
            [PANEL_TYPES.ADSHEL_CLASSIC]: AdshelClassic,
        };

        const PanelComponent = panelComponents[panelTypeToShow];
        return PanelComponent
            ? <PanelComponent mediaArray={filteredMedia} videoAutoPlayInterval={parameters.adCopyVideoAutoPlayIntervalMs} />
            : null;
    };

    if (!selectedOrderlineMetaData || !orderlinesData) {
        return null;
    }

    return (
        <div className={styles.container}>
            <div className={styles.secondRow}>
                <div className={styles.column} />
                <div className={styles.secondColumn}>
                    <div className={styles.secondColumnContainer}>
                        {showPanelFrame &&
                            <div className={styles.panelColumn}>
                                {renderPanel()}
                                <PanelMiniatures
                                    mediaArray={mediaArray}
                                    setPanelTypeToShow={setPanelTypeToShow}
                                    imageSources={IMAGE_SOURCES}
                                    campaignName={campaignName}
                                />
                            </div>
                        }
                        <div className={styles.resultDoubleRow}>
                            <div className={styles.progressRow}>
                                <Overview
                                    isSuccessful={isDeliverySuccessful}
                                    campaignStatus={campaignStatus}
                                    deliveryPercentage={deliveryPercentage}
                                    campaignStartsInDays={campaignStartsInDays}
                                    campaignPromiseType={campaignPromiseType}
                                    productPromise={campaignValues.fetchPromiseUserFriendlyDescriptionBasedOnCampaignPromise(campaignPromiseType)}
                                />
                                <div className={styles.spacingBetween} />
                                <div className={styles.resultDetails}>
                                    <Details
                                        campaignPromiseType={campaignPromiseType}
                                        orderData={orderData}
                                        selectedOrderlineMetaData={selectedOrderlineMetaData}
                                        orderlinesData={orderlinesData}
                                    />
                                    {overviewDetailsText &&
                                        campaignPromiseType === CAMPAING_PROMISE_TYPES.SE_CLASSIC_VAC &&
                                        campaignStatus !== CAMPAIGN_STATUS.NOT_STARTED &&
                                        <div className={styles.overviewDetailsText}>
                                            {overviewDetailsText}
                                        </div>
                                    }
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className={styles.column} />
            </div>
            <div className={styles.extraText}>
                {extraText}
            </div>

        </div>
    );
};

const PanelMiniatures = ({ mediaArray, setPanelTypeToShow, imageSources, campaignName }) => {

    // Skip rendering if there's only one panel type
    if (new Set(mediaArray.map(item => item.panelType)).size <= 1) {
        return null;
    }

    const panelConfig = {
        [PANEL_TYPES.ADSHEL_PLAY]: imageSources.ADSHEL_PANEL,
        [PANEL_TYPES.SPECTACULAR_PORTRAIT_PLAY]: imageSources.SPECTACULAR_PORTRAIT_PANEL,
        [PANEL_TYPES.BILLBOARD_PLAY]: imageSources.BILLBOARD_PANEL,
        [PANEL_TYPES.STORM_PLAY]: imageSources.STORM_PANEL,
        [PANEL_TYPES.ESCALATOR_CLASSIC]: imageSources.ESCALATOR_PANEL,
        [PANEL_TYPES.THREE_GROUP_CLASSIC]: imageSources.THREE_GROUP_PANEL,
    };

    const ThreeGroupStack = () => (
        <div
            style={{
                position: 'relative',
                width: '20px',
                height: '40px',
                cursor: 'pointer',
                marginTop: '4px'
            }}
            onClick={() => setPanelTypeToShow(PANEL_TYPES.THREE_GROUP_CLASSIC)}
        >
            <img
                src={imageSources.THREE_GROUP_PANEL}
                alt="Three group panel back"
                className={styles.small_threegroup_image}
                style={{
                    position: 'absolute',
                    top: '4px',
                    left: '4px',
                    margin: 0,
                    transform: 'none'
                }}
            />
            <img
                src={imageSources.THREE_GROUP_PANEL}
                alt="Three group panel middle"
                className={styles.small_threegroup_image}
                style={{
                    position: 'absolute',
                    top: '2px',
                    left: '2px',
                    margin: 0,
                    transform: 'none'
                }}
            />
            <img
                src={imageSources.THREE_GROUP_PANEL}
                alt="Three group panel front"
                className={styles.small_threegroup_image}
                style={{
                    position: 'absolute',
                    top: '0',
                    left: '0',
                    margin: 0,
                    transform: 'none'
                }}
            />
        </div>
    );

    return (
        <div className={styles.miniaturePanelContainer}>
            {Object.entries(panelConfig).map(([panelType, imageSrc]) =>
                mediaArray.some(item => item.panelType === panelType) && (
                    <span key={panelType}>
                        {panelType === PANEL_TYPES.THREE_GROUP_CLASSIC ?
                            <ThreeGroupStack />
                            :
                            <img
                                className={styles[`small_${panelType}_image`]}
                                onClick={() => setPanelTypeToShow(panelType)}
                                src={imageSrc}
                                alt={`${panelType} miniature`}
                            />
                        }
                    </span>
                )
            )}
        </div>
    );
};

export default SummaryPage;